import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  Button,
  Modal,
  ModalFooter,
  Select,
  Row,
  Col,
  Datepicker,
  MessageBanner,
} from '@folio/stripes/components';

function ClearanceModal(props) {
  const {
    onCloseModal,
    onConfirm,
    username,
    isLoading,
    isUndo = false,
    libraries = [],
    currentUser = {},
    locations = [],
  } = props;
  const [selectedLibrary, setSelectedLibrary] = useState('');

  useEffect(() => {
    if (!selectedLibrary && currentUser?.customFields?.defaultlibrary) {
      setSelectedLibrary(currentUser.customFields.defaultlibrary);
    }
  }, [currentUser?.customFields?.defaultlibrary]);
  const [clearanceDate, setClearanceDate] = useState(
    new Date().toISOString().split('T')[0],
  );
  const intl = useIntl();

  const isBackdated = useMemo(() => {
    if (!clearanceDate) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(clearanceDate);
    selectedDate.setHours(0, 0, 0, 0);
    return selectedDate < today;
  }, [clearanceDate]);

  const handleConfirm = () => {
    if (isUndo || selectedLibrary) {
      const dateToSend = clearanceDate ? new Date(clearanceDate) : new Date();
      onConfirm(selectedLibrary, dateToSend);
    }
  };

  const isConfirmDisabled = isLoading || (!isUndo && !selectedLibrary);

  const getLibraryDisplayName = (library) => {
    const translationKey = `ui-tenant-settings.loclibs.name.${library.name}`;
    const translatedName = intl.formatMessage(
      { id: translationKey },
      { defaultMessage: library.name },
    );
    return translatedName;
  };

  const renderFooter = (
    <ModalFooter>
      <Button
        data-test-clearance-confirm-button
        buttonStyle="danger"
        onClick={handleConfirm}
        disabled={isConfirmDisabled}
      >
        <FormattedMessage
          id={
            isUndo
              ? 'ui-users.clearance.undo.confirm.heading'
              : 'ui-users.clearance.modal.confirm'
          }
        />
      </Button>
      <Button
        data-test-clearance-cancel-button
        onClick={onCloseModal}
        disabled={isLoading}
      >
        <FormattedMessage id="ui-users.cancel" />
      </Button>
    </ModalFooter>
  );

  return (
    <Modal
      id="clearance-modal"
      data-test-clearance-modal
      open
      dismissible={false}
      label={
        <FormattedMessage
          id={
            isUndo
              ? 'ui-users.clearance.undo.confirm.heading'
              : 'ui-users.clearance.modal.title'
          }
        />
      }
      footer={renderFooter}
    >
      <FormattedMessage
        id={
          isUndo
            ? 'ui-users.clearance.undo.confirm.message'
            : 'ui-users.clearance.modal.message'
        }
        values={{ name: username }}
      />
      {!isUndo && (
        <>
          <br />
          <p
            style={{ color: '#d32f2f', fontWeight: 'bold', marginTop: '10px' }}
          >
            <FormattedMessage id="ui-users.clearance.modal.warning" />
          </p>
          <Row>
            <Col xs={6}>
              <Select
                label={<FormattedMessage id="ui-users.clearance.library" />}
                value={selectedLibrary}
                onChange={(e) => setSelectedLibrary(e.target.value)}
                required
              >
                <option value="">
                  <FormattedMessage id="ui-users.clearance.library.select" />
                </option>
                {libraries.map((library) => (
                  <option key={library.id} value={library.id}>
                    {getLibraryDisplayName(library)}
                  </option>
                ))}
              </Select>
            </Col>
            <Col xs={6}>
              <Datepicker
                label={<FormattedMessage id="ui-users.clearance.date" />}
                value={clearanceDate}
                onChange={(event, value) => setClearanceDate(value)}
                dateFormat="YYYY-MM-DD"
                backendDateStandard="YYYY-MM-DD"
                timeZone="UTC"
                required
                usePortal
              />
            </Col>
          </Row>
          {isBackdated && (
            <MessageBanner type="warning" style={{ marginTop: '10px' }}>
              <FormattedMessage
                id="ui-users.clearance.backdated.warning"
                values={{
                  date: intl.formatDate(clearanceDate, {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  }),
                }}
              />
            </MessageBanner>
          )}
        </>
      )}
    </Modal>
  );
}

ClearanceModal.propTypes = {
  username: PropTypes.string.isRequired,
  onCloseModal: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  isUndo: PropTypes.bool,
  libraries: PropTypes.arrayOf(PropTypes.object),
  currentUser: PropTypes.object,
  locations: PropTypes.arrayOf(PropTypes.object),
};

ClearanceModal.defaultProps = {
  isLoading: false,
  isUndo: false,
  libraries: [],
  currentUser: {},
  locations: [],
};

export default ClearanceModal;
